import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { BadgeProvider, useBadges } from '../../hooks/useBadges';
import { 
  Package, 
  PackagePlus,
  PackageMinus,
  Boxes,
  Truck,
  MessageSquare,
  Briefcase,
  Calendar
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import PanelHeader from '../../components/PanelHeader';

const DepotPanelContent = () => {
  const location = useLocation();
  const { getBadgeCount, markSeen } = useBadges();
  const [unreadCounts, setUnreadCounts] = useState({});

  const fetchUnreadCounts = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/unread-counts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadCounts(data);
      }
    } catch (error) {
      console.error('Bildirimler alınamadı:', error);
    }
  }, []);

  useEffect(() => {
    fetchUnreadCounts();
    const interval = setInterval(fetchUnreadCounts, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCounts]);

  useEffect(() => {
    fetchUnreadCounts();
  }, [location.pathname, fetchUnreadCounts]);

  const menuItems = [
    { path: '/depot/entry', icon: PackagePlus, label: 'Stok Giriş', menuKey: 'stock_entry', badgeKey: null },
    { path: '/depot/exit', icon: PackageMinus, label: 'Stok Çıkış', menuKey: 'stock_out', badgeKey: null },
    { path: '/depot/stock', icon: Boxes, label: 'Depo Stok', menuKey: 'inventory', badgeKey: null },
    { path: '/depot/incoming', icon: Truck, label: 'Gelecek Ürünler', menuKey: 'incoming', badgeKey: 'depot_incoming' },
    { path: '/depot/jobs', icon: Briefcase, label: 'İşler', menuKey: 'jobs', badgeKey: 'depot_jobs' },
    { path: '/depot/calendar', icon: Calendar, label: 'Takvim', menuKey: 'calendar', badgeKey: null },
    { path: '/depot/communication', icon: MessageSquare, label: 'İletişim', menuKey: 'communication', badgeKey: 'depot_inbox' },
  ];

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] pt-16" data-testid="depot-panel">
      {/* Header */}
      <PanelHeader
        title="Depo Paneli"
        titleIcon={Package}
        titleColor="text-emerald-600"
      />

      <div className="flex">
        {/* Sidebar */}
        <aside className="sticky top-16 h-[calc(100vh-4rem)] w-56 border-r border-[hsl(var(--border))] bg-white p-4">
          <nav className="space-y-2">
            {menuItems.map((item) => {
              // Badge sayısını belirle - önce yeni badge sisteminden
              let badgeCount = getBadgeCount(item.menuKey);
              if (badgeCount === 0 && item.badgeKey && unreadCounts[item.badgeKey] > 0) {
                badgeCount = unreadCounts[item.badgeKey];
              }
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => item.menuKey && markSeen(item.menuKey)}
                  className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    isActive(item.path)
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <item.icon className="h-5 w-5" />
                  <span>{item.label}</span>
                  {badgeCount > 0 && (
                    <Badge variant="destructive" className="ml-auto h-5 min-w-[20px] rounded-full px-1.5 text-xs flex items-center justify-center">
                      {badgeCount}
                    </Badge>
                  )}
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

const DepotPanel = () => {
  return (
    <BadgeProvider panel="depot">
      <DepotPanelContent />
    </BadgeProvider>
  );
};

export default DepotPanel;
